import Button from "@app/components/button";
import { addToast } from "@app/components/toast/toast.service";
import { RoutePath, SystemMessage, UserTypeOptions } from "@app/constants";
import CampaignService from "@app/services/http/campaign.service";
import BookService from "@app/services/http/book.service";
import {
  Campaign,
  CampaignNotification,
  CampaignPopupBanner,
  LabelValue,
  UpdateCampaign,
} from "@app/types";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { Form, Formik, FormikContextType } from "formik";
import { ChangeEvent, createRef, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  CampaignComponent,
  NotificationComponent,
  BannerComponent,
} from "./components";

interface UpdateFormValues {
  // Campaign
  name: string;
  discount: number;
  startDate: string;
  endDate: string;
  birthday: boolean;
  userType: LabelValue[];
  minPrice: number;
  maxPrice: number;
  // Notification
  notificationTitle: string;
  notificationBody: string;
  notificationNote: string;
  notificationRightIcon: string;
  notificationTimeSend: string;
  notificationRepeatDays: (LabelValue | string)[];
  isRepeatNotification: boolean;
  // Popup banner
  content: string;
  titleButton: string;
  actionButton: string;
  totalDisplayCount: number;
  totalDisplayTime: number;
  bannerMedia: string;
  // user filters
  userListenedToMinutes: number;
  kasIsGreaterThan: number;
  kasIsLessThan: number;
  hasPurchasedBooks: any[];
  notHasPurchasedBooks: any[];
  testingIds?: string;
}

const UpdateCampaignPage = () => {
  const formRef = createRef<FormikContextType<UpdateFormValues>>();

  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const type = (searchParams.get("type") || "campaigns") as
    | "campaigns"
    | "notifications"
    | "banners";

  const [campaignInfo, setCampaignInfo] = useState<Campaign | null>(null);
  const [notificationInfo, setNotificationInfo] =
    useState<CampaignNotification | null>(null);
  const [bannerInfo, setBannerInfo] = useState<CampaignPopupBanner | null>(
    null
  );

  const [isTypeUser, setIsTypeUser] = useState(false);
  const [isPriceRange, setIsPriceRange] = useState(false);
  const [update, forceUpdate] = useForceUpdate();
  const [bookOptions, setBookOptions] = useState<{ [key: number]: string }>({});

  // Function to load book information by IDs
  const loadBooksByIds = async (bookIds: number[]) => {
    if (!bookIds || bookIds.length === 0) return {};

    try {
      const response = await BookService.getRetailBooks(
        { keyword: "", pageNum: 1, pageSize: 1000 },
        undefined,
        false
      );

      const bookMap: { [key: number]: string } = {};

      if (response.data?.items) {
        response.data.items.forEach((book: any) => {
          if (bookIds.includes(book.value)) {
            bookMap[book.value] = book.label;
          }
        });
      }

      return bookMap;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error loading books:", error);

      return {};
    }
  };

  useEffect(() => {
    if (!campaignId) {
      return navigate(`/${RoutePath.CAMPAIGN_MANAGEMENT}`);
    }

    if (type === "campaigns") {
      CampaignService.getCampaignDetail(Number(campaignId)).then(
        async (res) => {
          const data = res.data;

          setCampaignInfo(data);

          if (data.targets?.userType?.length) {
            setIsTypeUser(true);
          }

          setIsPriceRange(
            Array.isArray(data.targets?.rangePrice) &&
              data.targets.rangePrice.length > 0
          );

          // Load book information
          const allBookIds = [
            ...(data.targets?.hasPurchasedBooks || []),
            ...(data.targets?.notHasPurchasedBooks || []),
          ];

          if (allBookIds.length > 0) {
            const bookMap = await loadBooksByIds(allBookIds);

            setBookOptions(bookMap);
          }
        }
      );
    }

    if (type === "notifications") {
      CampaignService.getNotificationDetail(String(campaignId)).then(
        async (res) => {
          const data = res.data;

          setNotificationInfo(data);

          if (data.targets?.userType?.length) {
            setIsTypeUser(true);
          }

          // Load book information
          const allBookIds = [
            ...(data.targets?.hasPurchasedBooks || []),
            ...(data.targets?.notHasPurchasedBooks || []),
          ];

          if (allBookIds.length > 0) {
            const bookMap = await loadBooksByIds(allBookIds);

            setBookOptions(bookMap);
          }
        }
      );
    }

    if (type === "banners") {
      CampaignService.getPopupBannerDetail(Number(campaignId)).then(
        async (res) => {
          const data = res.data;

          setBannerInfo(data);

          if (data.targets?.userType?.length) {
            setIsTypeUser(true);
          }

          // Load book information
          const allBookIds = [
            ...(data.targets?.hasPurchasedBooks || []),
            ...(data.targets?.notHasPurchasedBooks || []),
          ];

          if (allBookIds.length > 0) {
            const bookMap = await loadBooksByIds(allBookIds);

            setBookOptions(bookMap);
          }
        }
      );
    }
  }, [campaignId, type, update]);

  const handleTypeUserChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsTypeUser(e.target.checked);

    if (!e.target.checked && formRef.current) {
      formRef.current.setFieldValue("userType", []);
    }
  };

  const handlePriceRangeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsPriceRange(e.target.checked);

    if (!e.target.checked && formRef.current) {
      formRef.current.setFieldValue("minPrice", 0);
      formRef.current.setFieldValue("maxPrice", 0);
    }
  };

  const initialValues: UpdateFormValues = useMemo(() => {
    if (type === "campaigns") {
      return {
        name: campaignInfo?.name ?? "",
        discount: campaignInfo?.discount ?? 0,
        startDate: String(campaignInfo?.startDate ?? ""),
        endDate: String(campaignInfo?.endDate ?? ""),
        birthday: Boolean(campaignInfo?.targets?.birthday) ?? false,
        userType: Array.isArray(campaignInfo?.targets?.userType)
          ? campaignInfo!.targets.userType.map((type) => ({
              label: type,
              value: type,
            }))
          : [],
        minPrice: Array.isArray(campaignInfo?.targets?.rangePrice)
          ? Number(
              campaignInfo && campaignInfo.targets
                ? campaignInfo.targets.rangePrice[0]
                : 0
            )
          : 0,
        maxPrice: Array.isArray(campaignInfo?.targets?.rangePrice)
          ? Number(
              campaignInfo && campaignInfo.targets
                ? campaignInfo.targets.rangePrice[1]
                : 0
            )
          : 0,
        notificationTitle: "",
        notificationBody: "",
        notificationNote: "",
        notificationRightIcon: "",
        notificationTimeSend: "",
        notificationRepeatDays: [],
        isRepeatNotification: false,
        content: "",
        titleButton: "",
        actionButton: "",
        totalDisplayCount: 0,
        totalDisplayTime: 0,
        bannerMedia: "",
        userListenedToMinutes:
          campaignInfo?.targets?.userListenedToMinutes ?? 0,
        kasIsGreaterThan: campaignInfo?.targets?.kasIsGreaterThan ?? 0,
        kasIsLessThan: campaignInfo?.targets?.kasIsLessThan ?? 0,
        hasPurchasedBooks: Array.isArray(
          campaignInfo?.targets?.hasPurchasedBooks
        )
          ? (campaignInfo!.targets.hasPurchasedBooks
              .map((bookId) => {
                const bookName = bookOptions[bookId];

                return bookName
                  ? {
                      label: bookName,
                      value: bookId,
                    }
                  : null;
              })
              .filter(Boolean) as { label: string; value: number }[])
          : [],
        notHasPurchasedBooks: Array.isArray(
          campaignInfo?.targets?.notHasPurchasedBooks
        )
          ? (campaignInfo!.targets.notHasPurchasedBooks
              .map((bookId) => {
                const bookName = bookOptions[bookId];

                return bookName
                  ? {
                      label: bookName,
                      value: bookId,
                    }
                  : null;
              })
              .filter(Boolean) as { label: string; value: number }[])
          : [],
        testingIds:
          typeof (campaignInfo as any)?.testingIds === "string"
            ? ((campaignInfo as any).testingIds as string)
            : Array.isArray((campaignInfo as any)?.testingIds)
              ? ((campaignInfo as any).testingIds as any[]).join(", ")
              : "",
      };
    }

    if (type === "notifications") {
      return {
        name: "",
        discount: 0,
        startDate: "",
        endDate: "",
        birthday: Boolean(notificationInfo?.targets?.birthday) ?? false,
        userType: Array.isArray(notificationInfo?.targets?.userType)
          ? notificationInfo!.targets.userType.map((type) => ({
              label: type,
              value: type,
            }))
          : [],
        minPrice: 0,
        maxPrice: 0,
        notificationTitle: notificationInfo?.title ?? "",
        notificationBody: notificationInfo?.body ?? "",
        notificationNote: notificationInfo?.note ?? "",
        notificationRightIcon: notificationInfo?.rightIcon ?? "",
        notificationTimeSend: notificationInfo?.timeSend ?? "",
        notificationRepeatDays: Array.isArray(notificationInfo?.repeatDays)
          ? (notificationInfo?.repeatDays as unknown as string[]) || []
          : [],
        isRepeatNotification: Boolean(notificationInfo?.repeatDays?.length),
        content: "",
        titleButton: "",
        actionButton: "",
        totalDisplayCount: 0,
        totalDisplayTime: 0,
        bannerMedia: "",
        userListenedToMinutes:
          notificationInfo?.targets?.userListenedToMinutes ?? 0,
        kasIsGreaterThan: notificationInfo?.targets?.kasIsGreaterThan ?? 0,
        kasIsLessThan: notificationInfo?.targets?.kasIsLessThan ?? 0,

        hasPurchasedBooks: Array.isArray(
          notificationInfo?.targets?.hasPurchasedBooks
        )
          ? (notificationInfo!.targets.hasPurchasedBooks
              .map((bookId) => {
                const bookName = bookOptions[bookId];

                return bookName
                  ? {
                      label: bookName,
                      value: bookId,
                    }
                  : null;
              })
              .filter(Boolean) as { label: string; value: number }[])
          : [],

        notHasPurchasedBooks: Array.isArray(
          notificationInfo?.targets?.notHasPurchasedBooks
        )
          ? (notificationInfo!.targets.notHasPurchasedBooks
              .map((bookId) => {
                const bookName = bookOptions[bookId];

                return bookName
                  ? {
                      label: bookName,
                      value: bookId,
                    }
                  : null;
              })
              .filter(Boolean) as { label: string; value: number }[])
          : [],
        testingIds:
          typeof (notificationInfo as any)?.testingIds === "string"
            ? ((notificationInfo as any).testingIds as string)
            : Array.isArray((notificationInfo as any)?.testingIds)
              ? ((notificationInfo as any).testingIds as any[]).join(", ")
              : "",
      };
    }

    // banners
    return {
      name: "",
      discount: 0,
      startDate: bannerInfo?.startDate ?? "",
      endDate: bannerInfo?.endDate ?? "",
      birthday: Boolean(bannerInfo?.targets?.birthday) ?? false,
      userType: Array.isArray(bannerInfo?.targets?.userType)
        ? bannerInfo!.targets.userType.map((type) => ({
            label: type,
            value: type,
          }))
        : [],
      minPrice: 0,
      maxPrice: 0,
      notificationTitle: "",
      notificationBody: "",
      notificationNote: "",
      notificationRightIcon: "",
      notificationTimeSend: "",
      notificationRepeatDays: [],
      isRepeatNotification: false,
      content: bannerInfo?.content ?? "",
      titleButton: bannerInfo?.buttonTitle ?? "",
      actionButton: bannerInfo?.buttonAction ?? "",
      totalDisplayCount: Number(bannerInfo?.totalDisplayCount ?? 0),
      totalDisplayTime: Number(bannerInfo?.totalDisplayTime ?? 0),
      bannerMedia: bannerInfo?.media ?? "",
      userListenedToMinutes: bannerInfo?.targets?.userListenedToMinutes ?? 0,
      kasIsGreaterThan: bannerInfo?.targets?.kasIsGreaterThan ?? 0,
      kasIsLessThan: bannerInfo?.targets?.kasIsLessThan ?? 0,

      hasPurchasedBooks: Array.isArray(bannerInfo?.targets?.hasPurchasedBooks)
        ? (bannerInfo!.targets.hasPurchasedBooks
            .map((bookId) => {
              const bookName = bookOptions[bookId];

              return bookName
                ? {
                    label: bookName,
                    value: bookId,
                  }
                : null;
            })
            .filter(Boolean) as { label: string; value: number }[])
        : [],
      notHasPurchasedBooks: Array.isArray(
        bannerInfo?.targets?.notHasPurchasedBooks
      )
        ? (bannerInfo!.targets.notHasPurchasedBooks
            .map((bookId) => {
              const bookName = bookOptions[bookId];

              return bookName
                ? {
                    label: bookName,
                    value: bookId,
                  }
                : null;
            })
            .filter(Boolean) as { label: string; value: number }[])
        : [],
      testingIds:
        typeof (bannerInfo as any)?.testingIds === "string"
          ? ((bannerInfo as any).testingIds as string)
          : Array.isArray((bannerInfo as any)?.testingIds)
            ? ((bannerInfo as any).testingIds as any[]).join(", ")
            : "",
    };
  }, [type, campaignInfo, notificationInfo, bannerInfo, bookOptions]);

  const handleSubmit = async (values: UpdateFormValues) => {
    let payload: UpdateCampaign;

    if (type === "campaigns") {
      payload = {
        campaign: {
          id: Number(campaignId),
          name: values.name,
          discount: values.discount,
          startDate: String(values.startDate),
          endDate: String(values.endDate),
          testingIds: values.testingIds || "",
          targets: {
            birthday: !!values.birthday,
            userType:
              (values.userType || []).length > 0
                ? (values.userType || []).map((it) => String(it.value))
                : ["ALL"],
            ...(values.minPrice > 0 || values.maxPrice > 0
              ? {
                  rangePrice: [
                    Number(values.minPrice || 0),
                    Number(values.maxPrice || 0),
                  ],
                }
              : {}),
            userListenedToMinutes: Number(values.userListenedToMinutes || 0),
            kasIsGreaterThan: Number(values.kasIsGreaterThan || 0),
            kasIsLessThan: Number(values.kasIsLessThan || 0),
            hasPurchasedBooks: (values.hasPurchasedBooks || []).map((it: any) =>
              Number(it.value ?? it)
            ),
            notHasPurchasedBooks: (values.notHasPurchasedBooks || []).map(
              (it: any) => Number(it.value ?? it)
            ),
          },
        },
        notification: null,
        popupBanner: null,
      };
    } else if (type === "notifications") {
      const notificationPayload: any = {
        messageKey: String(campaignId),
        title: values.notificationTitle || "",
        body: values.notificationBody || "",
        note: values.notificationNote || "",
        rightIcon: values.notificationRightIcon || "",
        timeSend: values.notificationTimeSend || "",
        repeatDays: (values.notificationRepeatDays || []).map((d: any) =>
          typeof d === "string" ? d : d.value
        ),
        testingIds: values.testingIds || "",
        // Pass user targeting from UI for notifications
        targets: {
          birthday: !!values.birthday,
          userType:
            (values.userType || []).length > 0
              ? (values.userType || []).map((it: any) => String(it.value))
              : ["ALL"],
          userListenedToMinutes: Number(values.userListenedToMinutes || 0),
          kasIsGreaterThan: Number(values.kasIsGreaterThan || 0),
          kasIsLessThan: Number(values.kasIsLessThan || 0),
          hasPurchasedBooks: (values.hasPurchasedBooks || []).map((it: any) =>
            Number(it.value ?? it)
          ),
          notHasPurchasedBooks: (values.notHasPurchasedBooks || []).map(
            (it: any) => Number(it.value ?? it)
          ),
        },
      };

      payload = {
        campaign: null,
        notification: notificationPayload as any,
        popupBanner: null,
      };
    } else {
      const popupBannerPayload: any = {
        id: Number(campaignId),
        content: values.content || "",
        userType: Number(
          Array.isArray(values.userType) && values.userType.length > 0
            ? (values.userType[0] as LabelValue).value
            : 0
        ),
        media: values.bannerMedia || "",
        startDate: values.startDate || "",
        endDate: values.endDate || "",
        buttonTitle: values.titleButton || "",
        buttonAction: values.actionButton || "",
        totalDisplayCount: Number(values.totalDisplayCount || 0),
        totalDisplayTime: Number(values.totalDisplayTime || 0),
        testingIds: values.testingIds || "",
        // Pass user targeting from UI for popup banner
        targets: {
          birthday: !!values.birthday,
          userType:
            (values.userType || []).length > 0
              ? (values.userType || []).map((it: any) => String(it.value))
              : ["ALL"],
          userListenedToMinutes: Number(values.userListenedToMinutes || 0),
          kasIsGreaterThan: Number(values.kasIsGreaterThan || 0),
          kasIsLessThan: Number(values.kasIsLessThan || 0),
          hasPurchasedBooks: (values.hasPurchasedBooks || []).map((it: any) =>
            Number(it.value ?? it)
          ),
          notHasPurchasedBooks: (values.notHasPurchasedBooks || []).map(
            (it: any) => Number(it.value ?? it)
          ),
        },
      };

      payload = {
        campaign: null,
        notification: null,
        popupBanner: popupBannerPayload as any,
      };
    }

    await CampaignService.update(payload);
    addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
    forceUpdate();
  };

  const fetchUserOptions = async (inputValue: string) => {
    return UserTypeOptions.filter((option: any) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const handleGotoListCampaign = () => {
    navigate(`/${RoutePath.CAMPAIGN_MANAGEMENT}`);
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách campaign / Sửa</div>
      </div>
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnChange
        validateOnBlur
        enableReinitialize
      >
        <Form>
          {type === "campaigns" && (
            <div className="mt-6">
              <div className="grid grid-cols-2 gap-4">
                <CampaignComponent
                  isTypeUser={isTypeUser}
                  isPriceRange={isPriceRange}
                  onTypeUserChange={handleTypeUserChange}
                  onPriceRangeChange={handlePriceRangeChange}
                  fetchUserOptions={fetchUserOptions}
                />
              </div>
            </div>
          )}

          {type === "notifications" && (
            <div className="mt-6">
              <div className="grid grid-cols-2 gap-4">
                <NotificationComponent />
              </div>
            </div>
          )}

          {type === "banners" && (
            <div className="mt-6">
              <BannerComponent />
            </div>
          )}

          <div className="mt-6 flex justify-left gap-4">
            <Button
              type="submit"
              size="xs"
              label={"Cập nhật"}
              width={180}
              className="!h-[51px] !leading-[51px]"
              labelClassName="text-xl"
            />
            <Button
              size="xs"
              label="Hủy"
              theme="secondary"
              width={180}
              className="!h-[51px] !leading-[51px]"
              labelClassName="text-xl"
              onClick={handleGotoListCampaign}
            />
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default UpdateCampaignPage;
